/**
 * Landing — the pitch for strangers. The thread (/chat) is the app.
 *
 * Rules encoded here (from the approved landing mock):
 *  - identity is never the gate: the ask-bar starts an anonymous thread
 *  - the profile is pitched only where it earns its place ("keep your
 *    search"), and the promise copy sits at the exact moment of asking
 *  - returning visitors get their thread back in one tap (resume strip)
 */

import { useMemo, useState } from 'react';
import { useAdvisor, formatPrefsSummary } from '@/lib/advisor-context';
import { useBuyerAuth } from '@/lib/buyer-auth-context';
import { loadPersistedState } from '@/lib/session';

export function HomePage() {
  const { begin, beginWithAsk, prefs, messages } = useAdvisor();
  const { buyer, openSignIn, openProfile } = useBuyerAuth();
  const [ask, setAsk] = useState('');

  // Resume strip: only when this device already carries a real session.
  const hasSession = useMemo(() => {
    const saved = loadPersistedState();
    return (saved?.messages.length ?? 0) > 2 || messages.length > 2;
  }, [messages.length]);
  const briefSummary = formatPrefsSummary(prefs);

  const submitAsk = (e: React.FormEvent) => {
    e.preventDefault();
    if (ask.trim()) beginWithAsk(ask);
    else begin();
  };

  return (
    <div className="na-scroll ld-page">
      <header className="ld-hd">
        <div className="ld-brand">
          Naya<small>ADVISOR</small>
        </div>
        <div className="ld-hd-gap" />
        <button type="button" className="ld-hd-link" onClick={begin}>
          Open the chat
        </button>
        {buyer ? (
          <button type="button" className="ld-profile-chip" onClick={openProfile}>
            <span className="ld-profile-dot" />
            {buyer.masked}
          </button>
        ) : (
          <button type="button" className="ld-signin" onClick={openSignIn}>
            Sign in
          </button>
        )}
      </header>

      {hasSession && (
        <button type="button" className="ld-resume" onClick={begin}>
          <span className="ld-resume-tick">✓</span>
          <span>
            <b>Your thread is where you left it</b>
            <span className="ld-resume-sub">
              {briefSummary === 'Anonymous session' ? 'Anonymous session' : briefSummary} · tap to
              continue
            </span>
          </span>
          <span className="ld-resume-go">›</span>
        </button>
      )}

      <section className="ld-hero">
        <div className="ld-hero-copy">
          <div className="na-kicker">Bengaluru · Private home advisory</div>
          <h1>Ask before you reveal.</h1>
          <p className="ld-lede">
            Naya inspects homes with you — legal signals, cost rows, floor plans, honest tradeoffs
            and visit plans — while you stay anonymous. Your number reaches a builder only when you
            decide to visit.
          </p>
          <form className="ld-askbar" onSubmit={submitAsk}>
            <input
              type="text"
              value={ask}
              onChange={(e) => setAsk(e.target.value)}
              placeholder={'“2BHK near the airport — don’t share my number.”'}
              aria-label="Ask Naya"
            />
            <button type="submit">Ask Naya</button>
          </form>
          <div className="ld-hero-meta">
            No sign-up needed to start · profile is optional, for keeping your board
          </div>
          <div className="ld-stats">
            <div className="ld-stat">
              <b>₹0</b>
              <span>Brokerage</span>
            </div>
            <div className="ld-stat">
              <b>0</b>
              <span>Spam calls — number stays hidden</span>
            </div>
            <div className="ld-stat">
              <b>2 min</b>
              <span>To start</span>
            </div>
          </div>
        </div>
        <aside className="ld-story">
          <div className="na-kicker ld-kicker-gold">The reason Naya exists</div>
          <p>
            To see one floor plan, we had to give up our number. By Sunday, eleven builders had it —
            and still not one had told us what the place would truly cost.
          </p>
          <blockquote>
            Learn everything. Surrender nothing. Your name stays yours until the morning you choose
            to visit — and then we walk in beside you.
          </blockquote>
        </aside>
      </section>

      <section className="ld-acts">
        {ACTS.map((act) => (
          <div key={act.n} className="ld-act">
            <small>{act.n}</small>
            <h4>{act.title}</h4>
            <p>{act.desc}</p>
          </div>
        ))}
      </section>

      <section className="ld-keep">
        <div>
          <div className="na-kicker">Optional · one OTP</div>
          <h3>Keep your search. Stay unreachable.</h3>
          <p>
            A profile saves your board and brief on any device. We ask for an email or number{' '}
            <b>only to send you a sign-in code</b> — never to call you, never for marketing, never
            shared with builders. That&apos;s the whole use.
          </p>
        </div>
        <button
          type="button"
          className="ld-keep-cta"
          onClick={buyer ? openProfile : openSignIn}
        >
          {buyer ? 'Your profile' : 'Create your profile'}
        </button>
      </section>

      <footer className="ld-foot">
        <div className="ld-foot-inner">
          <div className="na-kicker ld-kicker-gold">The reveal</div>
          <h2>Reveal only when the visit is worth it.</h2>
          <p>
            Naya doesn&apos;t sell leads. You know what you&apos;re visiting, why you&apos;re
            visiting, and exactly who gets your number.
          </p>
          <div className="ld-scope">
            <b>Selected reveal</b>
            Your number goes only to the site team of the project you choose to visit — nothing
            before, no one else.
          </div>
        </div>
      </footer>
    </div>
  );
}

const ACTS = [
  { n: 'ACT 01', title: 'Stay anonymous', desc: 'No phone reveal just to see a plan or a price.' },
  { n: 'ACT 02', title: 'Ask naturally', desc: 'Budget, area, legal comfort emerge in conversation.' },
  { n: 'ACT 03', title: 'See proof', desc: 'Evidence with caveats, never brochure certainty.' },
  { n: 'ACT 04', title: 'Reveal with purpose', desc: 'Your number goes only to the visit you choose.' },
] as const;
